<template>
  <div ref="menu" class="menu">
    <nav v-if="showNavigation" class="menu__navigation" :aria-label="$t('landingNavigation.mainLabel')">
      <ul class="menu__links">
        <li class="menu__solutions">
          <button
            class="menu__solutions-trigger"
            :class="{ open: solutionsExpanded }"
            type="button"
            aria-controls="mobile-solutions-links"
            :aria-expanded="solutionsExpanded"
            @click="toggleSolutions"
          >
            {{$t('solutionsMenu.label')}}
          </button>
          <ul v-if="solutionsExpanded" id="mobile-solutions-links" class="menu__solutions-links">
            <li v-for="solution in solutions" :key="solution.id">
              <a :href="solution.href" :class="{active: pageIsActive(solution.href)}">
                {{$t(solution.titleKey)}}
              </a>
            </li>
          </ul>
        </li>
        <li class="menu__products">
          <button
            class="menu__products-trigger"
            :class="{ open: productsExpanded }"
            type="button"
            aria-controls="mobile-products-links"
            :aria-expanded="productsExpanded"
            @click="toggleProducts"
          >
            {{$t('productsMenu.label')}}
          </button>
          <ul v-if="productsExpanded" id="mobile-products-links" class="menu__products-links">
            <li v-for="product in products" :key="product.id">
              <a
                :href="product.href"
                :class="{ active: pageIsActive(product.href) }"
                @click="product.id === 'extension' && navigateToExtension($event)"
              >
                {{$t(product.titleKey)}}
              </a>
            </li>
          </ul>
        </li>
      </ul>
      <div class="menu__demo-wrap">
        <a class="menu__demo" :href="`https://form.typeform.com/to/${demoUrl}`" @click="openDemo">
          <span class="menu__demo-label">{{$t('try')}}</span>
        </a>
      </div>
    </nav>
    <LanguageSelector class="menu__languages" :languages="['en', 'ua', 'pl']" @languageSelected="saveLangToStarage"/>
  </div>
</template>

<script>
import { markRaw } from 'vue';
import LanguageSelector from '../shared/LanguageSelector.vue'
import { solutionLinks } from './solutionLinks.js'
import { productLinks } from './productLinks.js'
import { navigateToExtension } from './extensionNavigation.js'

export default {
  components: {
    LanguageSelector
  },
  props: {
    demoUrl: {
      type: String,
      default: ''
    },
    showNavigation: {
      type: Boolean,
      default: true
    }
  },
  emits: ['tryClick'],
  data(){
    return {
      solutionsExpanded: false,
      solutions: solutionLinks,
      productsExpanded: false,
      products: productLinks,
      demoPopup: null,
      touchY: null
    }
  },
  methods: {
    openDemo(event) {
      this.$emit('tryClick');
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0)
        return;

      // Read the current form on every click, including after a language change.
      // If the embed script is unavailable, the link still opens the form.
      if (!window.tf?.createPopup) return;
      this.demoPopup?.unmount();
      this.demoPopup = markRaw(
        window.tf.createPopup(this.demoUrl, {
          iframeProps: { title: this.$t('try') },
        })
      );
      event.preventDefault();
      this.demoPopup.open();
    },
    navigateToExtension(event){
      if (!navigateToExtension(event)) this.productsExpanded = false
    },
    toggleSolutions(){
      this.solutionsExpanded = !this.solutionsExpanded
    },
    toggleProducts(){
      this.productsExpanded = !this.productsExpanded
    },
    pageIsActive(pageUrl){
      return window.location.pathname === pageUrl
    },
    saveLangToStarage(lang){
      localStorage.setItem("drct-lang", lang)
    },
    isIOS(){
      return /iP(ad|hone|od)/.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    },
    handleTouchStart(event){
      this.touchY = event.touches.length === 1 ? event.touches[0].clientY : null
    },
    handleTouchMove(event){
      if (!event.cancelable || event.touches.length !== 1) return

      const menu = this.$refs.menu
      if (!menu) return

      const currentY = event.touches[0].clientY
      const previousY = this.touchY ?? currentY
      const deltaY = currentY - previousY
      this.touchY = currentY

      const maxScrollTop = menu.scrollHeight - menu.clientHeight
      const atTop = menu.scrollTop <= 1
      const atBottom = menu.scrollTop >= maxScrollTop - 1
      if (maxScrollTop <= 1 || (deltaY > 0 && atTop) || (deltaY < 0 && atBottom)) {
        event.preventDefault()
      }
    },
    clearTouch(){
      this.touchY = null
    }
  },
  mounted(){
    if (!this.isIOS()) return

    this.$refs.menu.addEventListener('touchstart', this.handleTouchStart, { passive: true })
    this.$refs.menu.addEventListener('touchmove', this.handleTouchMove, { passive: false })
    this.$refs.menu.addEventListener('touchend', this.clearTouch)
    this.$refs.menu.addEventListener('touchcancel', this.clearTouch)
  },
  beforeUnmount(){
    this.demoPopup?.unmount();
    if (!this.isIOS()) return

    this.$refs.menu?.removeEventListener('touchstart', this.handleTouchStart)
    this.$refs.menu?.removeEventListener('touchmove', this.handleTouchMove)
    this.$refs.menu?.removeEventListener('touchend', this.clearTouch)
    this.$refs.menu?.removeEventListener('touchcancel', this.clearTouch)
  }
}
</script>

<style lang="scss" scoped>
.menu {
  position: fixed;
  top: var(--mobile-menu-top, 54px);
  right: 0;
  left: 0;
  bottom: auto;
  height: var(--mobile-menu-height, calc(100vh - var(--mobile-menu-top, 54px)));
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  background: white;
  overscroll-behavior-y: contain;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  z-index: 9;
  --menu-horizontal-padding: var(--mobile-menu-gutter, 12px);

  @supports (height: 100dvh) {
    height: var(--mobile-menu-height, calc(100dvh - var(--mobile-menu-top, 54px)));
  }
}

.menu__navigation {
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
}

.menu__links {
  margin: 28px var(--menu-horizontal-padding) 0;
  padding: 0;
  list-style: none;
  border-top: 1px solid rgba(27, 39, 51, .16);
}

.menu__links-item,
.menu__solutions,
.menu__products {
  border-bottom: 1px solid rgba(27, 39, 51, .16);
}

.menu__links-item > a,
.menu__solutions-trigger,
.menu__products-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 58px;
  padding: 16px 8px;
  color: rgb(27, 39, 51);
  font-size: 14px;
  font-weight: bold;
  line-height: 1.25;
  text-align: left;
  text-decoration: none;
  text-transform: uppercase;
  opacity: .52;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  user-select: none;

  &:focus:not(:focus-visible) {
    outline: none;
  }
}

.menu__links-item > a {
  &:focus-visible {
    outline: 2px solid rgba(42, 138, 234, .55);
    outline-offset: -3px;
  }

  &.active {
    cursor: default;
    opacity: 1;
  }
}

.menu__solutions-trigger,
.menu__products-trigger {
  appearance: none;
  -webkit-appearance: none;
  border: 0;
  background: transparent;
  cursor: pointer;

  &::after {
    content: '';
    width: 12px;
    height: 6px;
    margin-left: 16px;
    background: url('../images/language-arrow.svg') center / 12px 6px no-repeat;
    opacity: .78;
  }

  &.open {
    opacity: 1;
  }

  &.open::after {
    transform: rotate(180deg);
  }

  &:focus-visible {
    outline: 2px solid rgba(42, 138, 234, .55);
    outline-offset: -3px;
  }
}

.menu__solutions-links,
.menu__products-links {
  margin: 0;
  padding: 0 8px 12px;
  list-style: none;

  li + li {
    margin-top: 2px;
  }

  a {
    display: block;
    padding: 10px 0;
    color: rgb(27, 39, 51);
    font-size: 14px;
    font-weight: 500;
    letter-spacing: .25px;
    line-height: 1.35;
    text-align: left;
    text-decoration: none;
    text-transform: uppercase;
    opacity: .52;
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
    user-select: none;

    &:focus:not(:focus-visible) {
      outline: none;
    }

    &.active {
      cursor: default;
      opacity: 1;
    }

    &:focus-visible {
      outline: 2px solid rgba(42, 138, 234, .55);
      outline-offset: 3px;
    }
  }
}

.menu__demo-wrap {
  margin: 24px var(--menu-horizontal-padding) 0;
}

.menu__demo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 38px;
  min-height: 0;
  box-sizing: border-box;
  padding: 10px 20px;
  color: white;
  background: #2a8aea;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: .6px;
  line-height: 20px;
  text-align: center;
  text-decoration: none;
  text-transform: uppercase;
  transition: background .15s ease;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  user-select: none;

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &:active {
    background: #1a7dd4;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: #1a7dd4;
    }
  }

  &:focus-visible {
    outline: 2px solid rgba(27, 39, 51, .6);
    outline-offset: -3px;
  }
}

.menu__demo-label {
  display: inline-block;
  transform: translateY(0);
}

.menu__languages {
  position: static;
  flex-shrink: 0;
  margin-top: auto;
  padding: 24px 0 calc(24px + env(safe-area-inset-bottom));

  ::v-deep(.language-selector__current) {
    display: none;
  }

  ::v-deep(.language-selector__dropdown) {
    position: static;
    display: block;
    width: 100%;
    min-width: 0;
    padding: 0;
    border: 0;
    border-radius: 0;
    box-shadow: none;
    visibility: visible;
    opacity: 1;
    pointer-events: auto;
    transform: none;
    transition: none;

    &::before {
      display: none;
    }

    ul {
      display: flex;
      justify-content: center;

      li {
        margin: 0 4px;
        -webkit-tap-highlight-color: transparent;
        touch-action: manipulation;
        user-select: none;
      }
    }
  }
}
</style>
